import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Wallet, Coins, ChartLineUp, ArrowsLeftRight, ChartBar,
  ShieldCheck, ShieldWarning, Info, Bank, CurrencyDollar,
  Handshake, GraduationCap, Briefcase, UserCircle, Users,
  PlayCircle, Sparkle, Trophy, Calendar, Star, Medal, Robot, Sliders,
  Globe, MapPin, Heart,
} from "@phosphor-icons/react";
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis, CartesianGrid,
} from "recharts";

/**
 * Tain Wallet — Revenue Sharing & Earnings Dashboard.
 *
 * Fetches a composite `/api/revenue-share/wallet-dashboard` payload:
 *   - 10 summary cards (balances, earnings, dates, eligibility badge)
 *   - 8 pillar breakdown cards (metric, weight, points, contribution, tier)
 *   - Live pool math (net revenue, pool USD, total score, user %, share)
 *   - Withdrawal module (available + min threshold gate)
 *
 * Every metric has a "View Calculation" modal showing raw value, weight,
 * formula and contribution — the transparency requirement from the product
 * spec.
 */

const TIER_COLORS = {
  Excellent: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  Good: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  Average: "bg-yellow-500/15 text-yellow-300 border-yellow-500/30",
  "Needs Improvement": "bg-red-500/15 text-red-300 border-red-500/30",
};

const PILLAR_ICONS = {
  referral_network: Handshake,
  content_creation: PlayCircle,
  engagement_received: Sparkle,
  audience_size: Users,
  service_usage: Briefcase,
  platform_engagement: ChartBar,
  credits_purchased: Coins,
  membership_longevity: UserCircle,
};

// Human-readable formula per pillar, kept in-sync with the backend engine.
const PILLAR_FORMULAS = {
  referral_network:
    "Σ (gen weight × active referral count). Gen weights: 1.0 / 0.6 / 0.4 / 0.25 / 0.15 for gens 1–5.",
  content_creation:
    "uploads × 5  +  unique upload days × 2  +  quality proxy  −  5 × outstanding reports.",
  engagement_received:
    "Σ (per-kind weight × count on your posts). Weights: like 1.0, comment 1.5, save 1.5, share 2.0, download 2.0, sound 2.5, gift 3.0.",
  audience_size:
    "followers  +  3 × new followers this month.",
  service_usage:
    "2 × (School + Jobs + Partnerships module interactions).",
  platform_engagement:
    "active days × 5  +  min(500, lifetime videos watched) × 0.05.",
  credits_purchased:
    "credits bought this month × 1.0  +  min(200, 0.02 × lifetime credits bought).",
  membership_longevity:
    "min(240, account age in months × 2).",
};

const fmtUsd = (n) => (n == null ? "—" : `$${Number(n).toFixed(2)}`);
const fmtDate = (iso) => {
  if (!iso) return "Available now";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

export default function WalletPage() {
  const { user, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openCalc, setOpenCalc] = useState(null); // pillar key or null

  const load = useCallback(async () => {
    try {
      const r = await api.get("/revenue-share/wallet-dashboard");
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load wallet");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return (
      <div className="p-6 md:p-8 max-w-6xl mx-auto">
        <p className="text-white/50 text-sm">Loading wallet…</p>
      </div>
    );
  }

  const eligibilityBadge = () => {
    if (data.eligibility === "eligible")
      return <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/40 gap-1"><ShieldCheck size={14} weight="fill" /> Eligible</Badge>;
    if (data.eligibility === "ineligible_low_score")
      return <Badge className="bg-yellow-500/15 text-yellow-300 border-yellow-500/30 gap-1"><ShieldWarning size={14} weight="fill" /> Score too low</Badge>;
    if (data.eligibility === "ineligible_low_activity")
      return <Badge className="bg-yellow-500/15 text-yellow-300 border-yellow-500/30 gap-1"><ShieldWarning size={14} weight="fill" /> Need more active days</Badge>;
    if (data.eligibility === "excluded_fraud")
      return <Badge className="bg-red-500/15 text-red-300 border-red-500/40 gap-1"><ShieldWarning size={14} weight="fill" /> Excluded</Badge>;
    return null;
  };

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto space-y-8" data-testid="wallet-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-black flex items-center gap-3">
            <Wallet size={32} weight="fill" className="text-cyan-300" />
            Tain Wallet
          </h1>
          <p className="text-white/60 mt-1">
            Revenue Sharing &amp; Earnings Dashboard —{" "}
            <span className="text-white/85 font-medium">{data.month}</span>
          </p>
        </div>
        <div>{eligibilityBadge()}</div>
      </header>

      {/* Summary cards — 10 tiles */}
      <section
        className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3"
        data-testid="wallet-summary-cards"
      >
        <Sum tid="sum-total" icon={Wallet} tint="from-cyan-500 to-emerald-500"
             label="Total Wallet Balance" value={fmtUsd(data.total_balance_usd)} />
        <Sum tid="sum-available" icon={CurrencyDollar} tint="from-emerald-500 to-teal-500"
             label="Available for Withdrawal" value={fmtUsd(data.available_for_withdrawal_usd)} />
        <Sum tid="sum-pending" icon={Info} tint="from-amber-500 to-orange-500"
             label="Pending Earnings" value={fmtUsd(data.pending_earnings_usd)}
             sub={`ongoing ${data.month}`} />
        <Sum tid="sum-confirmed" icon={ShieldCheck} tint="from-teal-500 to-emerald-600"
             label="Confirmed Earnings" value={fmtUsd(data.confirmed_earnings_usd)}
             sub="from past pools" />
        <Sum tid="sum-est-share" icon={ChartLineUp} tint="from-fuchsia-500 to-pink-500"
             label="Est. Monthly Share" value={fmtUsd(data.estimated_monthly_share_usd)}
             sub={`${data.user_pct_of_pool}% of pool`} />
        <Sum tid="sum-lifetime-revenue" icon={Trophy} tint="from-yellow-500 to-amber-500"
             label="Lifetime Revenue Earned" value={fmtUsd(data.lifetime_revenue_earned_usd)} />
        <Sum tid="sum-lifetime-withdrawals" icon={Bank} tint="from-indigo-500 to-blue-500"
             label="Lifetime Withdrawals" value={fmtUsd(data.lifetime_withdrawals_usd)} />
        <Sum tid="sum-eligibility" icon={ShieldCheck}
             tint={data.eligibility === "eligible" ? "from-emerald-500 to-green-600" : "from-red-500 to-rose-600"}
             label="Revenue Sharing Status"
             value={data.eligibility === "eligible" ? "Eligible" : "Not Eligible"}
             sub={data.excluded_reason || data.eligibility.replace(/_/g, " ")} />
        <Sum tid="sum-next-distribution" icon={Calendar} tint="from-purple-500 to-fuchsia-500"
             label="Next Distribution Date" value={fmtDate(data.next_distribution_at)} />
        <Sum tid="sum-next-withdrawal" icon={ArrowsLeftRight} tint="from-sky-500 to-cyan-500"
             label="Next Withdrawal Date"
             value={data.withdraw_ready ? "Available now" : fmtDate(data.next_withdrawal_at)}
             sub={data.withdraw_ready ? "You've met the threshold" : `Min ${data.min_withdrawal_credits} Credits`} />
      </section>

      {/* Live pool transparency */}
      <Card className="bg-[#121212] border-white/10">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading flex items-center gap-2">
            <ChartLineUp size={20} weight="fill" className="text-emerald-400" />
            Monthly Revenue Distribution — {data.month}
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="wallet-pool-transparency">
          <PoolStat label="Net Monthly Revenue" value={fmtUsd(data.revenue?.net_usd)} sub={`gross ${fmtUsd(data.revenue?.gross_usd)}`} />
          <PoolStat label={`Pool (${data.pool_percentage}%)`} value={fmtUsd(data.pool_usd)} sub={`${data.eligible_user_count} eligible users`} />
          <PoolStat label="Your Score / Total" value={`${data.user_weighted_score} / ${data.total_platform_score}`} sub={`${data.user_pct_of_pool}% of pool`} />
          <PoolStat label="Your Estimated Share" value={fmtUsd(data.estimated_monthly_share_usd)} sub={data.eligibility === "eligible" ? "eligible ✓" : "not eligible yet"} highlight />
        </CardContent>
      </Card>

      {/* 8 pillar breakdown */}
      <section>
        <h2 className="font-heading text-2xl font-bold mb-3">Revenue Sharing Breakdown</h2>
        <p className="text-white/60 text-sm mb-4">
          8 pillars power your monthly share. Each shows how you're doing and how it factors into your score.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="wallet-pillars">
          {Object.entries(data.factors_config || {}).map(([key, cfg]) => {
            const row = data.my_breakdown?.factor_breakdown?.[key] || {};
            const Icon = PILLAR_ICONS[key] || Sparkle;
            const tier = row.tier || "Needs Improvement";
            return (
              <div
                key={key}
                data-testid={`wallet-pillar-${key}`}
                className={`rounded-xl border p-4 space-y-3 ${cfg.enabled ? "bg-[#121212] border-white/10" : "bg-white/[0.02] border-white/5 opacity-50"}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center shrink-0">
                      <Icon size={20} weight="fill" className="text-white/80" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-heading font-bold truncate">{cfg.display_name}</p>
                      <p className="text-[11px] text-white/50 truncate">{cfg.description}</p>
                    </div>
                  </div>
                  <Badge className={`shrink-0 border ${TIER_COLORS[tier]}`} data-testid={`pillar-tier-${key}`}>{tier}</Badge>
                </div>

                <div className="grid grid-cols-4 gap-2 text-center">
                  <PillarMini label="Value" value={row.raw_value ?? 0} />
                  <PillarMini label="Weight" value={`${cfg.weight}%`} />
                  <PillarMini label="Points" value={row.points ?? 0} tint="text-yellow-300" />
                  <PillarMini label="Contribution" value={`${row.contribution_pct ?? 0}%`} tint="text-emerald-300" />
                </div>

                <Button
                  data-testid={`pillar-calc-btn-${key}`}
                  variant="outline"
                  size="sm"
                  onClick={() => setOpenCalc(key)}
                  className="w-full bg-transparent border-white/10 hover:bg-white/5"
                >
                  <Info size={14} className="mr-1.5" /> View Calculation
                </Button>
              </div>
            );
          })}
        </div>
      </section>

      {/* Optional profile completion so Country / Regional tiers work */}
      <LocationCapture user={user} />

      {/* AI Insights + Achievements + Rankings + Simulator + Charts */}
      <AiInsightsCard />
      <AchievementsCard />
      <LegalBadgesCard />
      <RankingsCard />
      <PerformanceChartsCard />
      <RevenueSimulatorCard factorsConfig={data.factors_config} baseline={data.my_breakdown} />

      {/* Withdrawal module */}
      <WithdrawalModule data={data} onDone={() => { load(); refresh?.(); }} />

      {/* View Calculation modal */}
      <Dialog open={!!openCalc} onOpenChange={(v) => !v && setOpenCalc(null)}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-lg" data-testid="pillar-calc-modal">
          {openCalc && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">
                  {data.factors_config?.[openCalc]?.display_name}
                </DialogTitle>
                <DialogDescription className="text-white/60">
                  {data.factors_config?.[openCalc]?.description}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <CalcRow label="Raw metric" value={data.my_breakdown.factor_breakdown[openCalc]?.raw_value} />
                  <CalcRow label="Weight (%)" value={`${data.factors_config[openCalc].weight}%`} />
                  <CalcRow label="Points earned" value={data.my_breakdown.factor_breakdown[openCalc]?.points} highlight />
                  <CalcRow label="Contribution to your score" value={`${data.my_breakdown.factor_breakdown[openCalc]?.contribution_pct}%`} highlight />
                  <CalcRow label="Enabled" value={data.factors_config[openCalc].enabled ? "Yes" : "No"} />
                  <CalcRow label="Tier vs cohort" value={data.my_breakdown.factor_breakdown[openCalc]?.tier} />
                </div>
                <div className="rounded-md bg-white/[0.03] border border-white/10 p-3">
                  <p className="text-[11px] uppercase tracking-widest text-white/50 font-bold mb-1">Formula</p>
                  <p className="text-xs text-white/75 leading-relaxed">
                    {PILLAR_FORMULAS[openCalc]}
                  </p>
                  <p className="text-[11px] text-white/40 mt-2 font-mono">
                    points = raw × (weight ÷ 100) = {data.my_breakdown.factor_breakdown[openCalc]?.raw_value}
                    {" × "}({data.factors_config[openCalc].weight} ÷ 100) ={" "}
                    <span className="text-yellow-300 font-bold">{data.my_breakdown.factor_breakdown[openCalc]?.points}</span>
                  </p>
                </div>
                <div className="rounded-md bg-emerald-500/10 border border-emerald-500/30 p-3 text-xs">
                  <p className="text-emerald-300 font-bold mb-1">Final revenue share formula</p>
                  <p className="font-mono text-white/80">
                    share = (your_score ÷ total_platform_score) × pool<br />
                    ({data.user_weighted_score} ÷ {data.total_platform_score}) × ${data.pool_usd} ={" "}
                    <span className="text-emerald-300 font-bold">${data.estimated_monthly_share_usd}</span>
                  </p>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Small building blocks
// ---------------------------------------------------------------------------
function Sum({ tid, icon: Icon, tint, label, value, sub }) {
  return (
    <div data-testid={tid} className="rounded-xl bg-[#121212] border border-white/10 p-4 space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-[0.15em] text-white/50 font-bold">{label}</p>
        <div className={`w-7 h-7 rounded-md bg-gradient-to-br ${tint} flex items-center justify-center`}>
          <Icon size={14} weight="fill" className="text-white" />
        </div>
      </div>
      <p className="font-heading text-xl font-black truncate">{value}</p>
      {sub && <p className="text-[10px] text-white/45 truncate">{sub}</p>}
    </div>
  );
}

function PoolStat({ label, value, sub, highlight = false }) {
  return (
    <div className={`rounded-lg p-3 ${highlight ? "bg-emerald-500/10 border border-emerald-500/30" : "bg-white/[0.03] border border-white/10"}`}>
      <p className="text-[10px] uppercase tracking-widest text-white/50 font-bold">{label}</p>
      <p className={`font-heading text-2xl font-black mt-1 ${highlight ? "text-emerald-300" : "text-white"}`}>{value}</p>
      {sub && <p className="text-[10px] text-white/45 mt-0.5">{sub}</p>}
    </div>
  );
}

function PillarMini({ label, value, tint }) {
  return (
    <div className="rounded-md bg-white/5 border border-white/10 p-2">
      <p className="text-[9px] uppercase tracking-widest text-white/40 font-bold">{label}</p>
      <p className={`text-sm font-heading font-bold mt-0.5 ${tint || "text-white"}`}>{value}</p>
    </div>
  );
}

function CalcRow({ label, value, highlight = false }) {
  return (
    <div className={`rounded-md p-2 ${highlight ? "bg-yellow-500/10 border border-yellow-500/30" : "bg-white/[0.03] border border-white/10"}`}>
      <p className="text-[10px] uppercase tracking-widest text-white/50 font-bold">{label}</p>
      <p className={`text-sm font-mono mt-0.5 ${highlight ? "text-yellow-300 font-bold" : "text-white"}`}>{value ?? "—"}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Withdrawal module
// ---------------------------------------------------------------------------
function WithdrawalModule({ data, onDone }) {
  const [method, setMethod] = useState("mobile_money");
  const [details, setDetails] = useState("");
  const [amount, setAmount] = useState(() => (data.available_for_withdrawal_usd || 0).toFixed(2));
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    try {
      const r = await api.get("/withdrawals");
      setHistory(r.data || []);
    } catch { /* no-op */ }
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const canWithdraw =
    data.withdraw_ready
    && parseFloat(amount) > 0
    && parseFloat(amount) <= data.available_for_withdrawal_usd
    && (details || "").trim().length > 3;

  const submit = async () => {
    if (!canWithdraw) return;
    setBusy(true);
    try {
      await api.post("/withdrawals", {
        amount: parseFloat(amount),
        method,
        payout_details: details,
      });
      toast.success("Withdrawal request submitted — admin will process it shortly.");
      setDetails("");
      onDone?.();
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="bg-[#121212] border-white/10" data-testid="wallet-withdraw-module">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading flex items-center gap-2">
          <Bank size={20} weight="fill" className="text-indigo-300" />
          Withdrawal
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <PoolStat label="Available" value={fmtUsd(data.available_for_withdrawal_usd)} sub="cash balance" highlight />
          <PoolStat label="Pending" value={fmtUsd(data.pending_withdrawals_usd)} sub="in review" />
          <PoolStat label="Min threshold" value={`${data.min_withdrawal_credits} Credits`} sub={`you have ${data.credits_balance}`} />
          <PoolStat label="Status" value={data.withdraw_ready ? "Ready" : "Locked"}
                    sub={data.withdraw_ready ? "requirements met" : "top up more Credits"} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-white/50 font-bold">Method</label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid="wd-method-select" className="bg-white/5 border-white/10 mt-1 min-h-[44px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10 text-white">
                <SelectItem value="mobile_money">Mobile Money (MoMo)</SelectItem>
                <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                <SelectItem value="paypal">PayPal</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-white/50 font-bold">Amount (USD)</label>
            <Input
              data-testid="wd-amount"
              type="number"
              min="0" step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="bg-white/5 border-white/10 mt-1 min-h-[44px]"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-white/50 font-bold">Payout details</label>
            <Input
              data-testid="wd-details"
              placeholder="Account #, MoMo #, or PayPal email"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              className="bg-white/5 border-white/10 mt-1 min-h-[44px]"
            />
          </div>
        </div>

        <Button
          data-testid="wd-submit-btn"
          onClick={submit}
          disabled={!canWithdraw || busy}
          className={`w-full min-h-[48px] font-heading font-bold gap-2 ${
            canWithdraw
              ? "bg-emerald-500 hover:bg-emerald-600 text-black"
              : "bg-white/5 text-white/40 cursor-not-allowed"
          }`}
        >
          <ShieldCheck size={18} weight="fill" />
          {busy ? "Submitting…" : canWithdraw ? `Withdraw ${fmtUsd(amount)}` : "Requirements not met"}
        </Button>

        {!data.withdraw_ready && (
          <p className="text-[11px] text-yellow-300/80 text-center">
            Withdraw is locked until your Credits balance ≥ {data.min_withdrawal_credits} and you have a cash balance to release.
          </p>
        )}

        {history.length > 0 && (
          <div className="pt-4 border-t border-white/10">
            <p className="text-[11px] uppercase tracking-widest text-white/50 font-bold mb-2">Recent requests</p>
            <div className="space-y-1.5" data-testid="wd-history">
              {history.slice(0, 6).map((h) => (
                <div key={h.withdrawal_id} className="flex items-center justify-between p-2 rounded bg-white/[0.03] text-xs">
                  <span>{fmtUsd(h.amount)} · {h.method}</span>
                  <span className="text-white/50">
                    {new Date(h.created_at).toLocaleDateString()} · {h.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="text-[10px] text-white/40 text-center pt-2">
          <Link to="/terms" className="underline hover:text-white/70">Terms of Service</Link>
          {" · "}Credits have no cash value; only the USD balance credited from monthly revenue shares can be withdrawn.
        </p>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// AI Insights — the 3 weakest pillars + a targeted next-best-action for each
// ============================================================================
function AiInsightsCard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/revenue-share/ai-insights");
        setData(r.data);
      } catch { /* silent */ }
    })();
  }, []);
  if (!data) return null;
  return (
    <Card className="bg-gradient-to-br from-fuchsia-500/10 to-indigo-500/10 border-fuchsia-500/20" data-testid="wallet-ai-insights">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading flex items-center gap-2">
          <Robot size={22} weight="fill" className="text-fuchsia-300" />
          AI Insights — next best actions
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {data.insights.length === 0 ? (
          <p className="text-white/60 text-sm">Nothing to recommend — you're doing great across all pillars.</p>
        ) : (
          data.insights.map((ins) => (
            <div key={ins.pillar} className="rounded-lg p-3 bg-black/30 border border-white/10"
                 data-testid={`ai-insight-${ins.pillar}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-heading font-bold text-sm">{ins.headline}</p>
                  <p className="text-white/70 text-xs mt-0.5">{ins.action}</p>
                </div>
                <Badge className="shrink-0 bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/40 text-[10px]">
                  {ins.weight_pct}% weight
                </Badge>
              </div>
            </div>
          ))
        )}
        <p className="text-[10px] text-white/40 italic">
          Recommendations are computed from your live pillar scores — not stored, not shared.
        </p>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Achievements — badge grid
// ============================================================================
const ACHIEVEMENT_ICON = {
  top_creator: Star,
  top_referrer: Handshake,
  community_builder: Users,
  education_ambassador: GraduationCap,
  job_connector: Briefcase,
  business_partner: Handshake,
  loyal_member: Medal,
  rising_star: Sparkle,
};

function AchievementsCard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/revenue-share/achievements");
        setData(r.data);
      } catch { /* silent */ }
    })();
  }, []);
  if (!data) return null;
  return (
    <Card className="bg-[#121212] border-white/10" data-testid="wallet-achievements">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading flex items-center gap-2">
          <Trophy size={22} weight="fill" className="text-yellow-300" />
          Achievements
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {data.achievements.map((a) => {
            const Icon = ACHIEVEMENT_ICON[a.key] || Star;
            return (
              <div
                key={a.key}
                data-testid={`achievement-${a.key}`}
                title={a.description}
                className={`rounded-xl border p-3 text-center flex flex-col items-center gap-2 ${
                  a.unlocked
                    ? "bg-yellow-500/10 border-yellow-500/40"
                    : "bg-white/[0.02] border-white/10 opacity-60"
                }`}
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  a.unlocked ? "bg-gradient-to-br from-yellow-400 to-amber-500" : "bg-white/5"
                }`}>
                  <Icon size={24} weight={a.unlocked ? "fill" : "regular"}
                        className={a.unlocked ? "text-black" : "text-white/60"} />
                </div>
                <p className="text-xs font-heading font-bold leading-tight">{a.name}</p>
                <p className="text-[10px] text-white/50 leading-snug line-clamp-2">{a.description}</p>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Legal badges — Moot Season prestige tiles
// ============================================================================
const LEGAL_BADGE_META = {
  first_win:          { label: "First Bench Win",  desc: "Complete your first perfect round.",    icon: Star },
  common_law_scholar: { label: "Common Law Scholar", desc: "50 correct in Common Law jurisdictions.", icon: GraduationCap },
  civil_law_scholar:  { label: "Civil Law Scholar",  desc: "50 correct in Civil Law jurisdictions.",  icon: GraduationCap },
  iron_streak:        { label: "Iron Streak",       desc: "15 in a row correct.",                   icon: Sparkle },
  senior_advocate:    { label: "Senior Advocate",   desc: "Reach Senior Advocate tier.",            icon: Trophy },
  round_1_survivor:   { label: "Round 1 Survivor",  desc: "Advance past Round 1 of Moot Season.",   icon: Medal },
  round_2_survivor:   { label: "Round 2 Survivor",  desc: "Advance past Round 2 of Moot Season.",   icon: Medal },
  round_3_survivor:   { label: "Round 3 Survivor",  desc: "Advance past Round 3 of Moot Season.",   icon: Medal },
  round_4_survivor:   { label: "Semi-Finalist",     desc: "Advance past Round 4 of Moot Season.",   icon: Medal },
  moot_champion:      { label: "Moot Champion",     desc: "Crowned Moot Season Champion.",          icon: Trophy },
};

function LegalBadgesCard() {
  const [player, setPlayer] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/legal-game/player");
        setPlayer(r.data);
      } catch { /* silent — not authenticated or no legal data yet */ }
    })();
  }, []);
  if (!player) return null;

  const earned = new Set(player.badges || []);
  const keys = Object.keys(LEGAL_BADGE_META);

  return (
    <Card className="bg-[#121212] border-emerald-500/20" data-testid="wallet-legal-badges">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading flex items-center gap-2">
          <Trophy size={22} weight="fill" className="text-emerald-300" />
          Legal Championship Badges
          <span className="ml-auto text-xs font-normal text-white/50">
            {earned.size} / {keys.length} earned
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {keys.map(k => {
            const meta = LEGAL_BADGE_META[k];
            const Icon = meta.icon;
            const unlocked = earned.has(k);
            const isChampion = k === "moot_champion" && unlocked;
            return (
              <div
                key={k}
                data-testid={`legal-badge-${k}`}
                title={meta.desc}
                className={`relative rounded-xl border p-3 text-center flex flex-col items-center gap-2 transition ${
                  isChampion
                    ? "bg-gradient-to-br from-amber-500/25 to-yellow-500/10 border-amber-400/70 shadow-lg shadow-amber-500/20"
                    : unlocked
                      ? "bg-emerald-500/10 border-emerald-500/40"
                      : "bg-white/[0.02] border-white/10 opacity-55"
                }`}
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  isChampion
                    ? "bg-gradient-to-br from-amber-400 to-yellow-500"
                    : unlocked
                      ? "bg-gradient-to-br from-emerald-400 to-teal-500"
                      : "bg-white/5"
                }`}>
                  <Icon size={22} weight={unlocked ? "fill" : "regular"}
                        className={unlocked ? "text-black" : "text-white/50"} />
                </div>
                <p className="text-xs font-heading font-bold leading-tight">{meta.label}</p>
                <p className="text-[10px] text-white/50 leading-snug line-clamp-2">{meta.desc}</p>
                {isChampion && (
                  <span className="absolute -top-2 -right-2 text-[9px] uppercase tracking-widest font-black bg-amber-400 text-black px-1.5 py-0.5 rounded-md">
                    Champion
                  </span>
                )}
              </div>
            );
          })}
        </div>
        {earned.size === 0 && (
          <p className="text-xs text-white/50 mt-3">
            Play the Legal Championship to earn your first badge.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Rankings — 6-tier leaderboards
// ============================================================================
const TIERS = [
  { key: "global",   label: "Global",   icon: Globe },
  { key: "country",  label: "Country",  icon: MapPin },
  { key: "regional", label: "Regional", icon: MapPin },
  { key: "friends",  label: "Friends",  icon: Heart },
  { key: "referral", label: "Referral", icon: Handshake },
  { key: "creator",  label: "Creator",  icon: PlayCircle },
];

function RankingsCard() {
  const [tier, setTier] = useState("global");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/revenue-share/rankings", { params: { tier, limit: 20 } });
        if (!cancelled) setData(r.data);
      } catch { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [tier]);

  return (
    <Card className="bg-[#121212] border-white/10" data-testid="wallet-rankings">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading flex items-center gap-2">
          <ChartBar size={22} weight="fill" className="text-emerald-300" />
          Rankings &amp; Leaderboards
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2" data-testid="rankings-tier-tabs">
          {TIERS.map((t) => {
            const Icon = t.icon;
            const active = tier === t.key;
            return (
              <button
                key={t.key}
                data-testid={`rankings-tier-${t.key}`}
                onClick={() => setTier(t.key)}
                className={`px-3 py-2 min-h-[40px] rounded-md text-xs font-heading font-bold flex items-center gap-1.5 ${
                  active
                    ? "bg-emerald-500 text-black"
                    : "bg-white/5 text-white/70 hover:bg-white/10"
                }`}
              >
                <Icon size={14} weight="bold" /> {t.label}
              </button>
            );
          })}
        </div>

        {data && (
          <div className="rounded-md bg-white/[0.03] border border-white/10 p-3 text-xs" data-testid="rankings-my-position">
            <span className="text-white/60">Your rank in {tier}:</span>{" "}
            {data.my_rank ? (
              <span className="text-emerald-300 font-bold">
                #{data.my_rank.toLocaleString()} of {data.total_in_tier.toLocaleString()}
                {" · Top "}{(100 - (data.my_percentile ?? 100)).toFixed(2)}%
              </span>
            ) : (
              <span className="text-white/40">unranked (need ≥ min activity/eligibility)</span>
            )}
          </div>
        )}

        {loading && <p className="text-white/50 text-sm">Loading leaderboard…</p>}

        {data && data.rows.length === 0 && !loading && (
          <p className="text-white/50 text-sm">No ranked users in this tier yet.</p>
        )}

        {data && data.rows.length > 0 && (
          <div className="space-y-1" data-testid="rankings-list">
            {data.rows.map((r) => (
              <div
                key={r.user_id}
                data-testid={`ranking-row-${r.user_id}`}
                className={`flex items-center gap-3 p-2 rounded ${
                  r.is_self ? "bg-emerald-500/10 border border-emerald-500/30" : "bg-white/[0.03]"
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-heading font-black text-sm ${
                  r.rank === 1 ? "bg-yellow-500 text-black"
                  : r.rank === 2 ? "bg-white/80 text-black"
                  : r.rank === 3 ? "bg-amber-600 text-white"
                  : "bg-white/10 text-white/70"
                }`}>
                  {r.rank}
                </div>
                {r.picture ? (
                  <img src={r.picture} alt="" className="w-8 h-8 rounded-full" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-white/10" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{r.name}{r.is_self ? " (you)" : ""}</p>
                  {r.country && <p className="text-[10px] text-white/40">{r.country}</p>}
                </div>
                <span className="text-xs font-heading font-bold text-yellow-300">
                  {r.weighted_score.toFixed(2)} pts
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Revenue Simulator — front-end-only sliders that recompute the score locally.
// Mirrors the backend formulas from routes/revenue_share.py so the projection
// is faithful without touching the DB. Purely for planning.
// ============================================================================
const SIMULATOR_INPUTS = [
  { key: "referrals_active", label: "Active referrals (5 gens combined)", max: 500, step: 1, unit: "" },
  { key: "uploads", label: "Approved uploads this month", max: 60, step: 1, unit: "" },
  { key: "upload_days", label: "Distinct upload days", max: 31, step: 1, unit: "" },
  { key: "engagement_actions", label: "Engagement actions received", max: 5000, step: 10, unit: "" },
  { key: "followers", label: "Total followers", max: 10000, step: 10, unit: "" },
  { key: "new_followers", label: "New followers this month", max: 2000, step: 5, unit: "" },
  { key: "service_actions", label: "School + Jobs + Partners actions", max: 300, step: 1, unit: "" },
  { key: "active_days", label: "Active days this month", max: 31, step: 1, unit: "" },
  { key: "videos_watched", label: "Videos watched (lifetime)", max: 5000, step: 10, unit: "" },
  { key: "credits_month", label: "Credits purchased this month", max: 5000, step: 25, unit: " cr" },
  { key: "credits_lifetime", label: "Credits purchased (lifetime)", max: 50000, step: 250, unit: " cr" },
  { key: "account_months", label: "Account age (months)", max: 120, step: 1, unit: " mo" },
];

function _computeSimulatorScore(inputs, factorsConfig) {
  // Faithful frontend mirror of the backend `PILLAR_COMPUTERS`. Weights come
  // from the live config so admin toggles are respected.
  const cfg = factorsConfig || {};
  const w = (k) => (cfg[k]?.enabled ? Number(cfg[k]?.weight || 0) : 0);

  const referral = inputs.referrals_active;              // approximation of Σgen×active
  const content = inputs.uploads * 5 + inputs.upload_days * 2;
  const engagement = inputs.engagement_actions;
  const audience = inputs.followers + inputs.new_followers * 3;
  const service = inputs.service_actions * 2;
  const platform = inputs.active_days * 5
                 + Math.min(500, inputs.videos_watched) * 0.05;
  const credits = inputs.credits_month + Math.min(200, inputs.credits_lifetime * 0.02);
  const longevity = Math.min(240, inputs.account_months * 2);

  const raw = {
    referral_network: referral,
    content_creation: content,
    engagement_received: engagement,
    audience_size: audience,
    service_usage: service,
    platform_engagement: platform,
    credits_purchased: credits,
    membership_longevity: longevity,
  };
  const pts = {};
  let total = 0;
  Object.entries(raw).forEach(([k, v]) => {
    const p = v * (w(k) / 100);
    pts[k] = p;
    total += p;
  });
  return { raw, pts, total };
}

function RevenueSimulatorCard({ factorsConfig, baseline }) {
  const initial = useMemo(() => ({
    referrals_active: 0, uploads: 0, upload_days: 0, engagement_actions: 0,
    followers: 0, new_followers: 0, service_actions: 0, active_days: 0,
    videos_watched: 0, credits_month: 0, credits_lifetime: 0,
    account_months: Math.max(0, Math.round(
      (baseline?.factor_breakdown?.membership_longevity?.raw_value || 0) / 2
    )),
  }), [baseline]);

  const [inputs, setInputs] = useState(initial);
  const [pool, setPool] = useState(null);

  useEffect(() => {
    // fetch pool once to project projected earnings
    (async () => {
      try {
        const r = await api.get("/revenue-share/pool-status");
        setPool(r.data);
      } catch { /* silent */ }
    })();
  }, []);

  const projection = useMemo(() => {
    const local = _computeSimulatorScore(inputs, factorsConfig);
    const currentTotal = pool?.total_platform_score || 0;
    const myBaseline = pool?.user_weighted_score || 0;
    // Assume the platform total updates with the diff
    const newTotal = Math.max(0.0001, currentTotal - myBaseline + local.total);
    const pctOfPool = (local.total / newTotal) * 100.0;
    const projectedShare = pool ? (pctOfPool / 100.0) * (pool.pool_usd || 0) : 0;
    // Estimated rank = 1 + count of eligible users with higher score.
    // We only know the aggregate `total_platform_score` and `eligible_user_count`,
    // so this is a rough estimate assuming a uniform distribution.
    const eligibleCount = pool?.eligible_user_count || 1;
    const meanScore = currentTotal / Math.max(1, eligibleCount);
    const estRank = local.total >= meanScore
      ? Math.max(1, Math.ceil(eligibleCount * (1 - local.total / (meanScore * 2))))
      : Math.ceil(eligibleCount * (0.5 + Math.max(0, meanScore - local.total) / (meanScore * 2)));
    return { ...local, newTotal, pctOfPool, projectedShare,
             eligibleCount, estRank: Math.max(1, Math.min(eligibleCount, estRank)) };
  }, [inputs, factorsConfig, pool]);

  const reset = () => setInputs(initial);

  return (
    <Card className="bg-[#121212] border-white/10" data-testid="wallet-simulator">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading flex items-center gap-2">
          <Sliders size={22} weight="fill" className="text-cyan-300" />
          Revenue Simulator
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-white/60 text-xs">
          Drag any slider to see how your monthly share would change. Uses the live admin weights.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {SIMULATOR_INPUTS.map((f) => (
            <div key={f.key} className="p-3 rounded bg-white/[0.03] border border-white/10">
              <div className="flex justify-between items-center mb-1">
                <label className="text-xs text-white/70">{f.label}</label>
                <span className="text-xs font-mono font-bold text-yellow-300">
                  {inputs[f.key]}{f.unit}
                </span>
              </div>
              <input
                type="range"
                min={0} max={f.max} step={f.step}
                value={inputs[f.key]}
                data-testid={`sim-${f.key}`}
                onChange={(e) => setInputs({ ...inputs, [f.key]: Number(e.target.value) })}
                className="w-full accent-cyan-400"
              />
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2 border-t border-white/10">
          <SimOut label="Contribution Score" value={projection.total.toFixed(2)} highlight />
          <SimOut label="% of Pool" value={`${projection.pctOfPool.toFixed(2)}%`} />
          <SimOut label="Projected Monthly (GHS)" value={`GHS ${(projection.projectedShare * 12.5).toFixed(2)}`}
                  sub={`≈ $${projection.projectedShare.toFixed(2)}`} />
          <SimOut label="Estimated Rank"
                  value={`#${projection.estRank.toLocaleString()} / ${projection.eligibleCount.toLocaleString()}`} />
        </div>

        <div className="flex items-center justify-between gap-3 pt-1">
          <Button
            data-testid="sim-reset-btn"
            variant="outline" size="sm"
            onClick={reset}
            className="border-white/20 bg-transparent hover:bg-white/5"
          >
            Reset sliders
          </Button>
          <p className="text-[10px] text-white/40 italic max-w-lg text-right">
            This simulator is for planning purposes only and does not alter stored platform data or actual earnings.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function SimOut({ label, value, sub, highlight = false }) {
  return (
    <div className={`rounded-lg p-3 ${
      highlight ? "bg-cyan-500/10 border border-cyan-500/30" : "bg-white/[0.03] border border-white/10"
    }`}>
      <p className="text-[10px] uppercase tracking-widest text-white/50 font-bold">{label}</p>
      <p className={`font-heading text-lg font-black mt-0.5 ${highlight ? "text-cyan-300" : "text-white"}`}>{value}</p>
      {sub && <p className="text-[10px] text-white/40 mt-0.5">{sub}</p>}
    </div>
  );
}


// ============================================================================
// Location Capture — prompt users to set country + region on first visit
// so the Country / Regional leaderboard tiers can group them.
// ============================================================================
const COMMON_COUNTRIES = [
  "Ghana", "Nigeria", "Kenya", "South Africa", "Uganda", "Tanzania",
  "Rwanda", "Ethiopia", "Zambia", "Zimbabwe", "Ivory Coast", "Senegal",
  "Cameroon", "Egypt", "Morocco", "United States", "United Kingdom",
  "Canada", "India", "Germany", "France", "Australia", "Other",
];

function LocationCapture({ user }) {
  const { refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [country, setCountry] = useState("");
  const [region, setRegion] = useState("");
  const [city, setCity] = useState("");
  const [dismissed, setDismissed] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    const missing = !user.country;
    const skipped = localStorage.getItem("tain_location_skipped") === "1";
    if (missing && !skipped && !dismissed) setOpen(true);
  }, [user, dismissed]);

  const save = async () => {
    if (!country) {
      toast.error("Pick a country");
      return;
    }
    setSaving(true);
    try {
      await api.put("/me/location",
        { country, region: region.trim(), city: city.trim() });
      toast.success("Thanks — your leaderboards are unlocked!");
      setOpen(false);
      refresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const skip = () => {
    localStorage.setItem("tain_location_skipped", "1");
    setDismissed(true);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) skip(); }}>
      <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md" data-testid="location-capture-modal">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <MapPin size={22} weight="fill" className="text-emerald-400" />
            Where are you based?
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Set your country and region to compete on the Country and Regional
            leaderboards. You can change this any time.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-white/50 font-bold">Country</label>
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger data-testid="loc-country" className="bg-white/5 border-white/10 mt-1 min-h-[44px]">
                <SelectValue placeholder="Select a country" />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10 text-white max-h-60">
                {COMMON_COUNTRIES.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-white/50 font-bold">Region / State</label>
            <Input
              data-testid="loc-region"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              placeholder="e.g. Greater Accra"
              className="bg-white/5 border-white/10 mt-1 min-h-[44px]"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-white/50 font-bold">City</label>
            <Input
              data-testid="loc-city"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="e.g. Accra"
              className="bg-white/5 border-white/10 mt-1 min-h-[44px]"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button
              data-testid="loc-save-btn"
              onClick={save}
              disabled={saving}
              className="flex-1 min-h-[44px] bg-emerald-500 hover:bg-emerald-600 text-black font-bold"
            >
              {saving ? "Saving…" : "Save"}
            </Button>
            <Button
              data-testid="loc-skip-btn"
              variant="outline"
              onClick={skip}
              className="border-white/20 bg-transparent hover:bg-white/5"
            >
              Skip
            </Button>
          </div>
          <p className="text-[10px] text-white/40 text-center">
            Only used for leaderboard grouping — never shared or sold.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ============================================================================
// Performance Charts — 8 series over 7d / 30d / 90d / year / lifetime.
// ============================================================================

const CHART_RANGES = [
  { key: "7d",       label: "7 Days"   },
  { key: "30d",      label: "30 Days"  },
  { key: "90d",      label: "90 Days"  },
  { key: "year",     label: "This Year"},
  { key: "lifetime", label: "Lifetime" },
];

const CHART_SERIES = [
  { key: "earnings_history",     label: "Earnings History",  chart: "area", tint: "#22c55e", format: "usd" },
  { key: "monthly_revenue_share",label: "Monthly Revenue Share", chart: "bar",  tint: "#a855f7", format: "usd" },
  { key: "referral_growth",      label: "Referral Growth",   chart: "line", tint: "#38bdf8", format: "int" },
  { key: "followers_growth",     label: "Followers Growth",  chart: "line", tint: "#f472b6", format: "int" },
  { key: "upload_trends",        label: "Content Uploads",   chart: "bar",  tint: "#f97316", format: "int" },
  { key: "engagement_trends",    label: "Engagement Trends", chart: "line", tint: "#eab308", format: "int" },
  { key: "credits_purchased",    label: "Credits Purchased", chart: "bar",  tint: "#fbbf24", format: "int" },
  { key: "withdrawal_history",   label: "Withdrawal History",chart: "bar",  tint: "#6366f1", format: "usd" },
];

function PerformanceChartsCard() {
  const [range, setRange] = useState("30d");
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/revenue-share/analytics", { params: { range } });
        if (!cancelled) setPayload(r.data);
      } catch { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [range]);

  return (
    <Card className="bg-[#121212] border-white/10" data-testid="wallet-charts">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading flex items-center gap-2">
          <ChartBar size={22} weight="fill" className="text-sky-300" />
          Performance Analytics
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2" data-testid="chart-range-tabs">
          {CHART_RANGES.map((r) => (
            <button
              key={r.key}
              data-testid={`chart-range-${r.key}`}
              onClick={() => setRange(r.key)}
              className={`px-3 py-2 min-h-[40px] rounded-md text-xs font-heading font-bold ${
                range === r.key
                  ? "bg-sky-500 text-black"
                  : "bg-white/5 text-white/70 hover:bg-white/10"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>

        {loading && <p className="text-white/50 text-sm">Loading charts…</p>}

        {payload && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="chart-grid">
            {CHART_SERIES.map((s) => (
              <ChartTile
                key={s.key}
                spec={s}
                data={(payload.series?.[s.key] || []).map((r) => ({ x: r.bucket, y: r.value }))}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ChartTile({ spec, data }) {
  const total = data.reduce((s, r) => s + (r.y || 0), 0);
  const formatted = spec.format === "usd"
    ? `$${total.toFixed(2)}`
    : total.toLocaleString();

  const Chart = spec.chart === "area" ? AreaChart
              : spec.chart === "bar" ? BarChart
              : LineChart;

  return (
    <div className="rounded-lg bg-white/[0.03] border border-white/10 p-3" data-testid={`chart-tile-${spec.key}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-heading font-bold">{spec.label}</p>
        <span className="text-xs font-mono font-bold" style={{ color: spec.tint }}>{formatted}</span>
      </div>
      <div style={{ width: "100%", height: 120 }}>
        {data.length === 0 ? (
          <div className="h-full flex items-center justify-center text-[11px] text-white/40">
            No data in this range
          </div>
        ) : (
          <ResponsiveContainer>
            <Chart data={data} margin={{ top: 4, right: 4, left: 4, bottom: 4 }}>
              <CartesianGrid stroke="#ffffff10" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="x" hide />
              <YAxis hide />
              <RTooltip
                contentStyle={{ background: "#121212", border: "1px solid #ffffff20", borderRadius: 8, fontSize: 11 }}
                labelStyle={{ color: "#fff8" }}
                formatter={(v) => (spec.format === "usd" ? `$${Number(v).toFixed(2)}` : v.toLocaleString())}
              />
              {spec.chart === "area" && (
                <Area type="monotone" dataKey="y" stroke={spec.tint}
                      fill={spec.tint} fillOpacity={0.18} strokeWidth={2} />
              )}
              {spec.chart === "line" && (
                <Line type="monotone" dataKey="y" stroke={spec.tint} strokeWidth={2} dot={false} />
              )}
              {spec.chart === "bar" && (
                <Bar dataKey="y" fill={spec.tint} radius={[3, 3, 0, 0]} />
              )}
            </Chart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

